import { useEffect, useRef, useState, type ReactNode } from "react";
import type { MouseEvent } from "react";
import { createPortal } from "react-dom";
import { Check, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  TruncatedText,
  hoverPopoverPanelClassName,
  truncatedTextClassName,
  truncatedTextContent,
  type TruncatedTextEllipsis,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { useAnchoredPanel } from "@/components/ui/overlay";

const COPIED_FEEDBACK_MS = 1800;

/** Safari fallback when `clipboard.writeText` is denied. Avoids lib.dom's deprecated `Document.execCommand` typing. */
function copySelectedTextLegacy(): boolean {
  const execCommand = Reflect.get(document, "execCommand");
  if (typeof execCommand !== "function") {
    return false;
  }
  return Boolean(execCommand.call(document, "copy"));
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    // Safari often denies writeText; use the legacy path below.
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, value.length);

  try {
    return copySelectedTextLegacy();
  } finally {
    document.body.removeChild(textarea);
  }
}

export interface ClickToCopyIdProps {
  value: string;
  /** Shown in the chip; full `value` is always copied. */
  displayValue?: ReactNode;
  className?: string;
  title?: string;
  /**
   * Where the ellipsis appears on overflow. `start` shows the last characters.
   * Pass `false` to render the display value without truncation.
   */
  ellipsis?: TruncatedTextEllipsis | false;
  /**
   * ``chip`` (default): bordered mono control with a copy icon.
   * ``plain``: text-only control; hover popover Copy → Copied!.
   */
  variant?: "chip" | "plain";
  /**
   * Plain only: show the Copy hover hint. Set false when another hover
   * surface (e.g. telemetry) owns the trigger; Copied! still appears on click.
   */
  hoverHint?: boolean;
  /**
   * Plain only: subtle hover border that marks the value as click-to-copy.
   * Disable for dense table cells that should stay flush with surrounding text.
   */
  hoverBox?: boolean;
  /**
   * Plain only: hover-hint label before copy (default: “Copy”).
   * Use a noun when the cell shows a truncated value (e.g. “Copy serial”).
   */
  copyLabel?: string;
}

export function ClickToCopyId({
  value,
  displayValue,
  className,
  title,
  ellipsis = "end",
  variant = "chip",
  hoverHint = true,
  hoverBox = true,
  copyLabel,
}: ClickToCopyIdProps) {
  const { t } = useTranslation("common");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);

  const isPlain = variant === "plain";
  const showCopyHint = isPlain && hoverHint;
  const showPanel = isPlain
    ? (showCopyHint && hovered) || copied
    : copied;

  const hidePanel = () => {
    if (isPlain) {
      setHovered(false);
      return;
    }
    setCopied(false);
  };

  const { panelStyle } = useAnchoredPanel({
    isOpen: showPanel,
    onClose: hidePanel,
    triggerRef,
    panelRef,
    placement: "above",
    align: "center",
    gap: 8,
    passive: true,
    closeOnOutsideClick: false,
  });

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_FEEDBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  if (!value) {
    return null;
  }

  const shown = displayValue ?? value;

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (copied) {
      return;
    }
    const ok = await copyTextToClipboard(value);
    if (ok) {
      setCopied(true);
      return;
    }
    toast.error(t("copyFailed"));
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(event) => void handleClick(event)}
        onMouseEnter={showCopyHint ? () => setHovered(true) : undefined}
        onMouseLeave={showCopyHint ? () => setHovered(false) : undefined}
        onFocus={showCopyHint ? () => setHovered(true) : undefined}
        onBlur={showCopyHint ? () => setHovered(false) : undefined}
        title={
          isPlain
            ? undefined
            : (title ?? (typeof shown !== "string" ? value : t("clickToCopy")))
        }
        aria-label={copied ? t("copied") : t("clickToCopyValue", { value })}
        className={cn(
          "group inline-flex min-w-0 max-w-full items-center text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
          isPlain
            ? cn(
                "gap-0 text-sm text-surface-900",
                hoverBox &&
                  cn(
                    "rounded-sm border border-transparent px-1 py-px",
                    "hover:border-surface-200",
                    copied && "border-success-300",
                  ),
              )
            : cn(
                "gap-1 rounded-md border px-1.5 py-0.5 font-mono text-sm",
                copied
                  ? "border-success-400 bg-success-50 text-success-800"
                  : "border-surface-200 bg-surface-50 text-surface-700 hover:border-surface-400 hover:bg-surface-100 hover:text-surface-900",
              ),
          className,
        )}
      >
        {typeof shown === "string" ? (
          ellipsis === false ? (
            <span className="whitespace-nowrap">{shown}</span>
          ) : (
            <TruncatedText title={isPlain ? undefined : value} ellipsis={ellipsis}>
              {shown}
            </TruncatedText>
          )
        ) : ellipsis === false ? (
          <span className="whitespace-nowrap" title={isPlain ? undefined : value}>
            {shown}
          </span>
        ) : (
          <span
            className={truncatedTextClassName(ellipsis)}
            title={isPlain ? undefined : value}
          >
            {truncatedTextContent(shown, ellipsis)}
          </span>
        )}
        {!isPlain ? (
          copied ? (
            <Check
              className="h-3.5 w-3.5 shrink-0 text-success-700"
              aria-hidden
            />
          ) : (
            <Copy
              className="h-3.5 w-3.5 shrink-0 text-surface-400 transition-colors group-hover:text-surface-700"
              aria-hidden
            />
          )
        ) : null}
      </button>
      {showPanel
        ? createPortal(
            <div
              ref={panelRef}
              role={copied ? "status" : "tooltip"}
              style={panelStyle}
              className={cn(
                "pointer-events-none z-[200] flex items-center gap-1.5 px-2.5 py-1.5",
                hoverPopoverPanelClassName,
              )}
            >
              {copied || !isPlain ? (
                <Check
                  className="h-3.5 w-3.5 shrink-0 text-success-600"
                  aria-hidden
                />
              ) : (
                <Copy
                  className="h-3.5 w-3.5 shrink-0 text-surface-500"
                  aria-hidden
                />
              )}
              <span className={copied ? "text-success-600" : undefined}>
                {isPlain
                  ? copied
                    ? t("copied")
                    : (copyLabel ?? t("copy"))
                  : t("copied")}
              </span>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
